<script>
  import { createEventDispatcher } from "svelte"
  import { DEFAULT_FONT_SIZE, MAX_FONT_SIZE, MIN_FONT_SIZE } from "./coordinates"

  export let saveStatus
  export let scale
  export let selected

  const ALIGNMENTS = [
    {
      lines: ["M3.75 6.75h16.5", "M3.75 12h9", "M3.75 17.25h13.5"],
      title: "Align left",
      value: "left",
    },
    {
      lines: ["M3.75 6.75h16.5", "M7.5 12h9", "M5.25 17.25h13.5"],
      title: "Align center",
      value: "center",
    },
    {
      lines: ["M3.75 6.75h16.5", "M11.25 12h9", "M6.75 17.25h13.5"],
      title: "Align right",
      value: "right",
    },
  ]

  const dispatch = createEventDispatcher()

  $: align = selected?.align ?? "left"

  function changeFontSize(event) {
    const value = Number.parseInt(event.currentTarget.value, 10)
    if (Number.isNaN(value)) return
    dispatch("fontsize", Math.min(Math.max(value, MIN_FONT_SIZE), MAX_FONT_SIZE))
  }

  // Enter applies the size instead of implicitly submitting the lease form.
  function fontSizeKeydown(event) {
    if (event.key !== "Enter") return
    event.preventDefault()
    changeFontSize(event)
  }
</script>

<div
  class="bg-white border-b flex flex-wrap gap-x-4 gap-y-2 items-center min-h-12 px-4 py-2 text-sm"
>
  <span
    class="border divide-x flex h-7 items-center overflow-hidden rounded {selected
      ? ''
      : 'opacity-40'}"
    title="Font size"
  >
    <span class="bg-slate-50 flex h-full items-center px-1.5 text-slate-500 text-xs">Aa</span>
    <input
      aria-label="Font size"
      class="border-none disabled:cursor-not-allowed focus:outline-none focus:ring-0 h-full px-1.5 text-sm w-14"
      disabled={!selected}
      max={MAX_FONT_SIZE}
      min={MIN_FONT_SIZE}
      type="number"
      value={selected ? (selected.font_size ?? DEFAULT_FONT_SIZE) : ""}
      on:change={changeFontSize}
      on:keydown={fontSizeKeydown}
    />
  </span>

  <span
    class="border divide-x flex items-center overflow-hidden rounded {selected
      ? ''
      : 'opacity-40'}"
  >
    {#each ALIGNMENTS as alignment (alignment.value)}
      <button
        class="disabled:cursor-not-allowed h-7 hover:bg-slate-100 w-8 {selected &&
        align === alignment.value
          ? 'bg-slate-200'
          : ''}"
        disabled={!selected}
        title={alignment.title}
        type="button"
        on:click={() => dispatch("align", alignment.value)}
      >
        <svg
          aria-hidden="true"
          class="mx-auto size-4"
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
          viewBox="0 0 24 24"
        >
          {#each alignment.lines as line (line)}
            <path d={line} stroke-linecap="round" stroke-linejoin="round" />
          {/each}
        </svg>
      </button>
    {/each}
  </span>

  <button
    class="border disabled:cursor-not-allowed disabled:opacity-40 h-7 hover:bg-red-50 rounded text-red-600 w-8"
    disabled={!selected}
    title="Delete this field placement (Delete)"
    type="button"
    on:click={() => dispatch("delete")}
  >
    <svg
      aria-hidden="true"
      class="mx-auto size-4"
      fill="none"
      stroke="currentColor"
      stroke-width="1.5"
      viewBox="0 0 24 24"
    >
      <path
        d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
  </button>

  <span class="flex gap-3 items-center ml-auto">
    {#if saveStatus === "saving"}
      <span class="text-slate-500">Saving…</span>
    {:else if saveStatus === "saved"}
      <span class="text-emerald-600">Saved</span>
    {:else if saveStatus === "error"}
      <span class="flex gap-2 items-center">
        <span class="font-medium text-red-600">Couldn't save</span>
        <button class="link" type="button" on:click={() => dispatch("retry")}>Retry</button>
      </span>
    {/if}
  </span>

  <span class="border divide-x flex h-7 items-center overflow-hidden rounded">
    <button
      class="h-full hover:bg-slate-100 leading-none w-8"
      title="Zoom out"
      type="button"
      on:click={() => dispatch("zoomout")}
    >
      −
    </button>
    <span
      class="bg-slate-50 flex h-full items-center justify-center tabular-nums text-slate-600 text-xs w-12"
    >
      {Math.round(scale * 100)}%
    </span>
    <button
      class="h-full hover:bg-slate-100 leading-none w-8"
      title="Zoom in"
      type="button"
      on:click={() => dispatch("zoomin")}
    >
      +
    </button>
  </span>
</div>
